/**
 * @copyright WaterStreet. All rights reserved.
 */

/* eslint-disable max-len */
/* eslint-disable no-bitwise */

import {
	Injectable
} from '@angular/core';
import {
	AppConstants
} from '@shared/constants/app.constants';
import {
	FormlyConstants
} from '@shared/constants/formly.constants';
import {
	AnyHelper
} from '@shared/helpers/any.helper';
import {
	MenuItem
} from 'primeng/api';
import {
	Subject
} from 'rxjs';

/* eslint-enable max-len */

/**
 * A singleton service for computing and tracking which navigation
 * tab and section keys contain fields with rule violations.
 *
 * @export
 * @class NavigationViolationService
 */
@Injectable({
	providedIn: 'root'
})
export class NavigationViolationService
{
	/**
	 * Gets or sets the set of tab keys that have violations.
	 *
	 * @type {Set<string>}
	 * @memberof NavigationViolationService
	 */
	public violatedTabKeys: Set<string> = new Set<string>();

	/**
	 * Gets or sets the set of section keys that have violations.
	 *
	 * @type {Set<string>}
	 * @memberof NavigationViolationService
	 */
	public violatedSectionKeys: Set<string> = new Set<string>();

	/**
	 * Gets or sets the set of repeater data-keys that have
	 * violations within their child fields.
	 *
	 * @type {Set<string>}
	 * @memberof NavigationViolationService
	 */
	public violatedRepeaterKeys: Set<string> = new Set<string>();

	/**
	 * Gets or sets the set of nested tab keys (inside repeaters)
	 * that have violations within their child fields.
	 *
	 * @type {Set<string>}
	 * @memberof NavigationViolationService
	 */
	public violatedNestedTabKeys: Set<string> = new Set<string>();

	/**
	 * Gets a subject that emits after every violation key computation.
	 * Subscribe to trigger change detection in OnPush components.
	 *
	 * @type {Subject<void>}
	 * @memberof NavigationViolationService
	 */
	public readonly violationKeysUpdated: Subject<void> =
		new Subject<void>();

	/**
	 * Pattern matching a trailing repeater count suffix such as
	 * " (3)" or "  (1)" appended to section title text.
	 * Uses \s+ to handle one or more whitespace characters that may
	 * appear when the count is rendered inside a child span element.
	 *
	 * @static
	 * @type {RegExp}
	 * @memberof NavigationViolationService
	 */
	private static readonly repeaterCountSuffix: RegExp =
		/\s+\(\d+\)$/;

	/**
	 * Attribute selector for elements with a tab-key attribute.
	 *
	 * @static
	 * @type {string}
	 * @memberof NavigationViolationService
	 */
	private static readonly tabKeySelector: string =
		`[${FormlyConstants.attributeKeys.tabKey}]`;

	/**
	 * Element selector for the custom-repeater component.
	 *
	 * @static
	 * @type {string}
	 * @memberof NavigationViolationService
	 */
	private static readonly repeaterSelector: string =
		FormlyConstants.customControls.customRepeater;

	/**
	 * Gets or sets the allowed navigation labels used to filter
	 * section resolution to only base-level nav sections.
	 *
	 * @type {Set<string>}
	 * @memberof NavigationViolationService
	 */
	private navigationLabels: Set<string> = new Set<string>();

	/**
	 * Determines whether any of the violation tracking sets
	 * currently contain entries. A true result indicates at
	 * least one navigation key was marked as violated by the
	 * most recent computation.
	 *
	 * @returns {boolean}
	 * True if any violation keys are currently tracked.
	 * @memberof NavigationViolationService
	 */
	public hasViolatedKeys(): boolean
	{
		return this.violatedTabKeys.size > 0
			|| this.violatedSectionKeys.size > 0
			|| this.violatedRepeaterKeys.size > 0
			|| this.violatedNestedTabKeys.size > 0;
	}

	/**
	 * Performs a cheap check to determine whether a container has
	 * any fields with invalid validation state. Uses querySelector
	 * which stops at the first match rather than scanning all
	 * elements, making it suitable as an early-exit gate.
	 *
	 * @param {ParentNode} container
	 * The DOM scope to check.
	 * @returns {boolean}
	 * True if any invalid field exists within the container.
	 * @memberof NavigationViolationService
	 */
	public hasInvalidFields(
		container: ParentNode): boolean
	{
		const ngInvalid: string =
			FormlyConstants.angularClasses.ngInvalid;
		const pInvalid: string =
			AppConstants.cssClasses.invalid;

		return !AnyHelper.isNull(
			container.querySelector(
				`.${ngInvalid}, .${pInvalid}`));
	}

	/**
	 * Computes which tab, section, repeater, and nested tab keys
	 * contain invalid form fields by scanning the DOM for elements
	 * with data-key attributes that have the ng-invalid CSS class
	 * on themselves or their descendants.
	 *
	 * @param {ParentNode} container
	 * The DOM scope to scan. Defaults to the full document.
	 * @memberof NavigationViolationService
	 */
	public computeViolatedNavigationKeys(
		container: ParentNode = document): void
	{
		this.violatedTabKeys.clear();
		this.violatedSectionKeys.clear();
		this.violatedRepeaterKeys.clear();
		this.violatedNestedTabKeys.clear();

		const dataKeySelector: string =
			`[${FormlyConstants.attributeKeys.dataKey}]`;
		const fieldElements: Element[] =
			Array.from(
				container.querySelectorAll(dataKeySelector));

		const sectionTitleCache: Map<Element, Element[]> =
			new Map<Element, Element[]>();

		for (const fieldElement of fieldElements)
		{
			if (!this.isElementInvalid(fieldElement))
			{
				continue;
			}

			this.resolveTabKey(
				fieldElement,
				this.violatedTabKeys);
			this.resolveSectionKey(
				fieldElement,
				this.violatedSectionKeys,
				sectionTitleCache);
			this.resolveRepeaterKey(
				fieldElement,
				this.violatedRepeaterKeys);
			this.resolveNestedTabKey(
				fieldElement,
				this.violatedNestedTabKeys);
		}

		this.violationKeysUpdated.next();
	}

	/**
	 * Stamps violation style classes on MenuItem items for dropdown
	 * navigation display. Items matching violated tab or section keys
	 * receive the violation badge class. Sub-items are checked against
	 * section keys only.
	 *
	 * @param {MenuItem[]} items
	 * The menu items to stamp.
	 * @memberof NavigationViolationService
	 */
	public stampViolationStyleOnMenuItems(
		items: MenuItem[]): void
	{
		const violationClass: string =
			AppConstants.cssClasses.hasNavigationViolation;

		for (const item of items)
		{
			const hasViolation: boolean =
				this.violatedTabKeys.has(item.label)
					|| this.violatedSectionKeys.has(item.label);

			item.badgeStyleClass = hasViolation
				? violationClass
				: AppConstants.empty;

			if (!AnyHelper.isNullOrEmptyArray(item.items))
			{
				for (const subItem of item.items)
				{
					subItem.badgeStyleClass =
						this.violatedSectionKeys.has(subItem.label)
							? violationClass
							: AppConstants.empty;
				}
			}
		}
	}

	/**
	 * Stamps or removes a violation dot on individual repeater
	 * accordion item headers based on whether each item contains
	 * invalid fields.
	 *
	 * @param {ParentNode} container
	 * The DOM scope to scan. Defaults to the full document.
	 * @memberof NavigationViolationService
	 */
	public stampViolationDotOnRepeaters(
		container: ParentNode = document): void
	{
		const indicatorClass: string =
			AppConstants.cssClasses.violationIndicator;
		const headerTextClass: string =
			AppConstants.cssClasses.pAccordionHeaderText;
		const accordionTabs: Element[] =
			Array.from(
				container.querySelectorAll(
					`${NavigationViolationService
						.repeaterSelector} ${
						FormlyConstants.elements.pAccordionTab}`));

		for (const tab of accordionTabs)
		{
			const headerText: Element =
				tab.querySelector(`.${headerTextClass}`);

			if (AnyHelper.isNull(headerText))
			{
				continue;
			}

			const content: Element =
				tab.querySelector(
					`.${AppConstants.cssClasses.pAccordionContent}`);
			const hasInvalidFields: boolean =
				!AnyHelper.isNull(content)
					&& this.isElementInvalid(content);

			const existingIndicator: Element =
				headerText.querySelector(`.${indicatorClass}`);

			if (hasInvalidFields)
			{
				if (AnyHelper.isNull(existingIndicator))
				{
					const indicator: HTMLElement =
						document.createElement('i');
					indicator.setAttribute('aria-hidden', 'true');
					indicator.classList.add(
						...AppConstants.cssClasses
							.fontAwesomeAsterisk.split(' '),
						indicatorClass);
					headerText.appendChild(indicator);
				}
			}
			else if (!AnyHelper.isNull(existingIndicator))
			{
				existingIndicator.remove();
			}
		}
	}

	/**
	 * Sets the allowed navigation labels used to constrain section
	 * resolution. Only section titles matching these labels will be
	 * considered during violation key computation. This ensures
	 * sub-sections within repeaters resolve to their parent
	 * base-level navigation section.
	 *
	 * @param {string[]} labels
	 * The navigation labels from tab and section menu items.
	 * @memberof NavigationViolationService
	 */
	public setNavigationLabels(
		labels: string[]): void
	{
		this.navigationLabels = new Set<string>(labels);
	}

	/**
	 * Clears all violation state by resetting both key sets to empty.
	 *
	 * @memberof NavigationViolationService
	 */
	public clear(): void
	{
		this.violatedTabKeys.clear();
		this.violatedSectionKeys.clear();
		this.violatedRepeaterKeys.clear();
		this.violatedNestedTabKeys.clear();
		this.navigationLabels.clear();
	}

	/**
	 * Resolves the tab key for a DOM element and adds it to the
	 * provided set.
	 *
	 * @param {Element} element
	 * The element to resolve the tab key for.
	 * @param {Set<string>} tabKeys
	 * The set to add the resolved tab key to.
	 * @returns {boolean}
	 * True if a tab key was resolved.
	 * @memberof NavigationViolationService
	 */
	private resolveTabKey(
		element: Element,
		tabKeys: Set<string>): boolean
	{
		const tabContent: Element =
			this.findOutermostTabKeyAncestor(element);

		if (AnyHelper.isNull(tabContent))
		{
			return false;
		}

		const tabKey: string =
			tabContent.getAttribute(
				FormlyConstants.attributeKeys.tabKey)
				?? AppConstants.empty;

		if (AnyHelper.isNullOrEmpty(tabKey))
		{
			return false;
		}

		tabKeys.add(tabKey);

		return true;
	}

	/**
	 * Resolves the section key for a DOM element and adds it to the
	 * provided set.
	 *
	 * @param {Element} element
	 * The element to resolve the section key for.
	 * @param {Set<string>} sectionKeys
	 * The set to add the resolved section key to.
	 * @returns {boolean}
	 * True if a section key was resolved.
	 * @memberof NavigationViolationService
	 */
	private resolveSectionKey(
		element: Element,
		sectionKeys: Set<string>,
		sectionTitleCache: Map<Element, Element[]>): boolean
	{
		const sectionKey: string =
			this.findSectionKeyForElement(
				element, sectionTitleCache);

		if (AnyHelper.isNullOrEmpty(sectionKey))
		{
			return false;
		}

		sectionKeys.add(sectionKey);

		return true;
	}

	/**
	 * Finds the section key for a DOM element by walking up the DOM tree
	 * and searching backward through siblings for the nearest preceding
	 * section title element.
	 *
	 * @param {Element} element
	 * The element to find the section key for.
	 * @param {Map<Element, Element[]>} sectionTitleCache
	 * Cache of section title elements per container.
	 * @returns {string}
	 * The section label, or empty string if not found.
	 * @memberof NavigationViolationService
	 */
	private findSectionKeyForElement(
		element: Element,
		sectionTitleCache: Map<Element, Element[]>): string
	{
		const container: Element =
			element.closest(
				NavigationViolationService.tabKeySelector)
				?? element.closest(
					FormlyConstants.elements.formlyForm);

		if (AnyHelper.isNull(container))
		{
			return AppConstants.empty;
		}

		let sectionTitles: Element[];
		if (sectionTitleCache.has(container))
		{
			sectionTitles = sectionTitleCache.get(container);
		}
		else
		{
			const sectionTitleSelector: string =
				`.${AppConstants.cssClasses.sectionTitle}`
					+ ` .${AppConstants.cssClasses.sectionTitleText}`;

			sectionTitles =
				Array.from(
					container.querySelectorAll(sectionTitleSelector));
			sectionTitleCache.set(container, sectionTitles);
		}

		const precedingTitles: Element[] = [];

		for (const titleElement of sectionTitles)
		{
			const position: number =
				element.compareDocumentPosition(titleElement);

			const isPreceding: boolean =
				(position & Node.DOCUMENT_POSITION_PRECEDING) !== 0;

			if (isPreceding)
			{
				precedingTitles.push(titleElement);
			}
		}

		const hasNavLabels: boolean =
			this.navigationLabels.size > 0;

		for (let index:
			number = precedingTitles.length - 1;
			index >= 0; index--)
		{
			const sectionLabel: string =
				this.normalizeSectionTitleText(
					precedingTitles[index]);

			if (!hasNavLabels
				|| this.navigationLabels.has(sectionLabel))
			{
				return sectionLabel;
			}
		}

		if (this.navigationLabels.has(
			AppConstants.basePageSections.topIdentifier)
			&& !this.isInsideNestedTab(container)
			&& this.containerHasNavigationSections(
				sectionTitles))
		{
			return AppConstants.basePageSections.topIdentifier;
		}

		return AppConstants.empty;
	}

	/**
	 * Determines whether a container element is inside a nested
	 * tab by checking if its parent is a custom-nested-tab-content
	 * component. Fields inside nested tabs should not fall back
	 * to the "Top" section because "Top" only represents controls
	 * before the first section title on a base-level tab or page.
	 *
	 * @param {Element} container
	 * The container element to check.
	 * @returns {boolean}
	 * True if the container is inside a nested tab.
	 * @memberof NavigationViolationService
	 */
	private isInsideNestedTab(
		container: Element): boolean
	{
		return container.parentElement?.tagName.toLowerCase()
			=== FormlyConstants.customControls
				.customNestedTabContent;
	}

	/**
	 * Determines whether any section title in the provided list
	 * matches a registered navigation label. This ensures the
	 * "Top" fallback only applies to containers that actually
	 * have navigation-registered sections, since "Top" represents
	 * content before the first named navigation section.
	 *
	 * @param {Element[]} sectionTitles
	 * The section title elements within the container.
	 * @returns {boolean}
	 * True if at least one section title matches a navigation label.
	 * @memberof NavigationViolationService
	 */
	private containerHasNavigationSections(
		sectionTitles: Element[]): boolean
	{
		for (const titleElement of sectionTitles)
		{
			const label: string =
				this.normalizeSectionTitleText(titleElement);

			if (this.navigationLabels.has(label))
			{
				return true;
			}
		}

		return false;
	}

	/**
	 * Finds the outermost ancestor element with a tab-key attribute
	 * by walking up through nested tab containers. This ensures
	 * fields inside nested tabs resolve to the top-level tab.
	 *
	 * @param {Element} element
	 * The element to find the outermost tab ancestor for.
	 * @returns {Element}
	 * The outermost tab-key ancestor, or null if none found.
	 * @memberof NavigationViolationService
	 */
	private findOutermostTabKeyAncestor(
		element: Element): Element
	{
		let current: Element =
			element.closest(
				NavigationViolationService.tabKeySelector);

		if (AnyHelper.isNull(current))
		{
			return null;
		}

		let outer: Element = current;

		while (!AnyHelper.isNull(current.parentElement))
		{
			const next: Element =
				current.parentElement.closest(
					NavigationViolationService.tabKeySelector);

			if (AnyHelper.isNull(next))
			{
				break;
			}

			outer = next;
			current = next;
		}

		return outer;
	}

	/**
	 * Resolves all ancestor repeater data-keys for a DOM element
	 * by walking up through every custom-repeater host and reading
	 * the data-key attribute from each section title child.
	 *
	 * @param {Element} element
	 * The element to resolve repeater keys for.
	 * @param {Set<string>} repeaterKeys
	 * The set to add the resolved repeater keys to.
	 * @memberof NavigationViolationService
	 */
	private resolveRepeaterKey(
		element: Element,
		repeaterKeys: Set<string>): void
	{
		let repeaterHost: Element =
			element.closest(
				NavigationViolationService
					.repeaterSelector);

		while (!AnyHelper.isNull(repeaterHost))
		{
			const sectionTitle: Element =
				repeaterHost.querySelector(
					`.${AppConstants.cssClasses.sectionTitle}`
						+ `[${FormlyConstants
							.attributeKeys.dataKey}]`);

			if (!AnyHelper.isNull(sectionTitle))
			{
				const dataKey: string =
					sectionTitle.getAttribute(
						FormlyConstants
							.attributeKeys.dataKey)
						?? AppConstants.empty;

				if (!AnyHelper.isNullOrEmpty(dataKey))
				{
					repeaterKeys.add(dataKey);
				}
			}

			repeaterHost =
				repeaterHost.parentElement?.closest(
					NavigationViolationService
						.repeaterSelector) ?? null;
		}
	}

	/**
	 * Extracts and normalizes a section title label from its DOM
	 * element by trimming whitespace and removing any trailing
	 * repeater count suffix (e.g., " (3)").
	 *
	 * @param {Element} titleElement
	 * The section title text element to normalize.
	 * @returns {string}
	 * The normalized label text, or empty string when absent.
	 * @memberof NavigationViolationService
	 */
	private normalizeSectionTitleText(
		titleElement: Element): string
	{
		return titleElement.textContent?.trim()
			?.replace(
				NavigationViolationService.repeaterCountSuffix,
				AppConstants.empty)
			?? AppConstants.empty;
	}

	/**
	 * Checks whether an element or any of its descendants has the
	 * Angular ng-invalid or PrimeNG p-invalid CSS class indicating a
	 * failed validation.
	 *
	 * @param {Element} element
	 * The element to check.
	 * @returns {boolean}
	 * True if the element or a descendant is invalid.
	 * @memberof NavigationViolationService
	 */
	private isElementInvalid(
		element: Element): boolean
	{
		const ngInvalid: string =
			FormlyConstants.angularClasses.ngInvalid;
		const pInvalid: string =
			AppConstants.cssClasses.invalid;

		return element.classList.contains(ngInvalid)
			|| element.classList.contains(pInvalid)
			|| !AnyHelper.isNull(
				element.querySelector(
					`.${ngInvalid}, .${pInvalid}`));
	}

	/**
	 * Resolves the nested tab key for a field element by finding the
	 * nearest ancestor [tab-key] div that is a direct child of a
	 * custom-nested-tab-content component. This correctly identifies
	 * the active tab panel regardless of how deeply nested the field is
	 * (e.g. inside a sub-repeater within a nested tab).
	 *
	 * @param {Element} element
	 * The field element to resolve the nested tab key for.
	 * @param {Set<string>} nestedTabKeys
	 * The set to add the resolved nested tab key to.
	 * @memberof NavigationViolationService
	 */
	private resolveNestedTabKey(
		element: Element,
		nestedTabKeys: Set<string>): void
	{
		let ancestor: Element = element.parentElement;

		while (!AnyHelper.isNull(ancestor))
		{
			if (ancestor.hasAttribute(FormlyConstants.attributeKeys.tabKey)
				&& ancestor.parentElement?.tagName.toLowerCase()
					=== FormlyConstants.customControls
						.customNestedTabContent)
			{
				const key: string =
					ancestor.getAttribute(
						FormlyConstants.attributeKeys.tabKey);

				if (!AnyHelper.isNullOrEmpty(key))
				{
					nestedTabKeys.add(key);
				}

				return;
			}

			ancestor = ancestor.parentElement;
		}
	}
}